import { useCallback } from 'react';

import {
  SCHEMA_VERSION,
  useTracking,
} from '@xing-com/crate-communication-tracking';
import type { NWTEvent } from '@xing-com/crate-communication-tracking';
import { useFeatures, useViewerData } from '@xing-com/hub';

type SharedEventProps =
  | 'application'
  | 'channel'
  | 'sentBy'
  | 'userId'
  | 'schemaVersion'
  | 'context'
  | 'type';

const defaultProps = (
  userId: string | undefined,
  userType: string
): Pick<NWTEvent, SharedEventProps> => ({
  application: 'wbm',
  channel: 'wbm/CompaniesOverview',
  schemaVersion: SCHEMA_VERSION,
  userId,
  sentBy: 'companies',
  context: userType,
  type: 'nwt',
});

type OmitFromUnion<T, K extends keyof any> = T extends any ? Omit<T, K> : never;

export type NwtTrackingProps = OmitFromUnion<NWTEvent, SharedEventProps>;

/**
 * `useNwtTrackingCompanies` returns a function to track a NWT event
 */
export const useNwtTrackingCompanies = () => {
  const { track } = useTracking();
  const { data: viewerData } = useViewerData();
  const features = useFeatures();
  const userId = viewerData?.user?.id;
  const isBrandManagerEditor = features.data?.isBrandManagerEditor;
  const userType = isBrandManagerEditor ? 'companies_editor' : 'companies_user';

  return useCallback(
    (nwtTrackingProps: NwtTrackingProps) => {
      track({
        ...defaultProps(userId, userType),
        ...nwtTrackingProps,
      });
    },
    [track, userId, userType]
  );
};
